import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FolderOpen, Search, ChevronUp, ChevronDown } from 'lucide-react';
import { useBuckets, useDeleteBucket } from '../hooks/useBuckets.js';
import { useCompanies } from '../hooks/useCompanies.js';
import { toast } from '../lib/toast.js';
import { useWorkspaceStore } from '../stores/workspace.js';
import type { Bucket } from '@mayksel/types';
import BucketModal from '../components/buckets/BucketModal.js';

type SortDir = 'asc' | 'desc';

const STATUS_COLORS: Record<string, string> = {
  active: 'var(--color-success)',
  won: 'var(--color-brand)',
  lost: 'var(--color-danger)',
  on_hold: 'var(--color-amber)',
  complete: 'var(--color-text-muted)',
};

export default function Buckets() {
  const { t } = useTranslation('buckets');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const workspace = useWorkspaceStore((s) => s.workspace);

  const [search, setSearch] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingBucket, setEditingBucket] = useState<Bucket | null>(null);

  const { data, isLoading, error } = useBuckets({
    ...(search ? { search } : {}),
    ...(companyFilter ? { companyId: companyFilter } : {}),
    ...(statusFilter ? { status: statusFilter } : {}),
    sortBy,
    sortDir,
  });

  const { data: companiesData } = useCompanies({ limit: 200 });
  const deleteBucket = useDeleteBucket();

  const handleSort = useCallback((col: string) => {
    if (sortBy === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(col);
      setSortDir('asc');
    }
  }, [sortBy]);

  async function handleDelete(bucket: Bucket) {
    if (!confirm(t('detail.delete_confirm', { name: bucket.name }))) return;
    try {
      await deleteBucket.mutateAsync(bucket.id);
      toast.success(t('toast.deleted'));
    } catch (_err) {
      toast.error(t('toast.delete_error'));
    }
  }

  function handleModalClose() {
    setModalOpen(false);
    setEditingBucket(null);
  }

  const SortIcon = ({ col }: { col: string }) => {
    if (sortBy !== col) return null;
    return sortDir === 'asc'
      ? <ChevronUp size={14} style={{ marginLeft: 4 }} />
      : <ChevronDown size={14} style={{ marginLeft: 4 }} />;
  };

  const bucketLabelPlural = workspace?.bucketLabelPlural ?? t('list.title');
  const hasFilters = !!(search || companyFilter || statusFilter);

  return (
    <div className="page-enter">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-6)' }}>
        <h1 className="page-title">{bucketLabelPlural}</h1>
        <button
          onClick={() => setModalOpen(true)}
          style={{
            display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
            padding: 'var(--space-2) var(--space-4)', background: 'var(--color-brand)',
            color: 'var(--color-text-inverse)', border: 'none',
            borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)',
            fontWeight: 'var(--font-medium)', cursor: 'pointer',
          }}
        >
          + {t('list.add_button')}
        </button>
      </div>

      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 'var(--space-3)', marginBottom: 'var(--space-4)', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
          <Search size={16} style={{
            position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
            color: 'var(--color-text-muted)', pointerEvents: 'none',
          }} />
          <input
            type="text" value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('list.search_placeholder')}
            style={{
              width: '100%', paddingLeft: 36, paddingRight: 'var(--space-3)',
              paddingTop: 'var(--space-2)', paddingBottom: 'var(--space-2)',
              border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)',
              fontSize: 'var(--text-sm)', color: 'var(--color-text)',
              background: 'var(--color-bg)', boxSizing: 'border-box',
            }}
          />
        </div>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          aria-label={t('list.filter_status')}
          style={selectStyle}
        >
          <option value="">{t('list.all_statuses')}</option>
          {(['active', 'on_hold', 'won', 'lost', 'complete'] as const).map((s) => (
            <option key={s} value={s}>{t(`status.${s}`)}</option>
          ))}
        </select>

        <select
          value={companyFilter}
          onChange={(e) => setCompanyFilter(e.target.value)}
          aria-label={t('list.filter_company')}
          style={selectStyle}
        >
          <option value="">{t('list.all_companies')}</option>
          {companiesData?.data.map((co) => (
            <option key={co.id} value={co.id}>{co.name}</option>
          ))}
        </select>
      </div>

      {/* Loading */}
      {isLoading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="skeleton-line" style={{ height: 52, borderRadius: 'var(--radius-md)' }} />
          ))}
        </div>
      )}

      {/* Error */}
      {error && !isLoading && (
        <div style={{
          padding: 'var(--space-6)', background: 'var(--color-danger-bg)',
          borderRadius: 'var(--radius-lg)', color: 'var(--color-danger-text)',
          fontSize: 'var(--text-sm)', textAlign: 'center',
        }}>
          {tc('errors.generic')}
        </div>
      )}

      {/* Empty */}
      {!isLoading && !error && data?.data.length === 0 && (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', padding: 'var(--space-16) var(--space-8)',
          textAlign: 'center', border: '1px dashed var(--color-border)',
          borderRadius: 'var(--radius-xl)',
        }}>
          <FolderOpen size={48} style={{ marginBottom: 'var(--space-4)', color: 'var(--color-gray-300)' }} />
          <h2 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-medium)', color: 'var(--color-text)', marginBottom: 'var(--space-2)' }}>
            {hasFilters ? tc('empty.no_results') : t('list.empty_title')}
          </h2>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', maxWidth: 320, marginBottom: 'var(--space-5)' }}>
            {hasFilters ? tc('empty.try_adjusting') : t('list.empty_body')}
          </p>
          {!hasFilters && (
            <button
              onClick={() => setModalOpen(true)}
              style={{
                padding: 'var(--space-2) var(--space-5)', background: 'var(--color-brand)',
                color: 'var(--color-text-inverse)', border: 'none',
                borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)', cursor: 'pointer',
              }}
            >
              + {t('list.empty_cta')}
            </button>
          )}
        </div>
      )}

      {/* Table */}
      {!isLoading && !error && data && data.data.length > 0 && (
        <div style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden', background: 'var(--color-bg)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--color-bg-subtle)', borderBottom: '1px solid var(--color-border)' }}>
                {[
                  { key: 'name', label: t('columns.name') },
                  { key: 'company', label: t('columns.company'), noSort: true },
                  { key: 'status', label: t('columns.status') },
                  { key: 'value', label: t('columns.value'), noSort: true },
                  { key: 'expected_close', label: t('columns.expected_close'), noSort: true },
                ].map(({ key, label, noSort }) => (
                  <th
                    key={key}
                    onClick={() => !noSort && handleSort(key)}
                    style={{
                      padding: 'var(--space-3) var(--space-4)', textAlign: 'left',
                      fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)',
                      color: 'var(--color-text-secondary)', textTransform: 'uppercase',
                      letterSpacing: 'var(--tracking-wide)', cursor: noSort ? 'default' : 'pointer',
                      userSelect: 'none', whiteSpace: 'nowrap',
                    }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center' }}>
                      {label}
                      <SortIcon col={key} />
                    </span>
                  </th>
                ))}
                <th style={{ width: 80 }} />
              </tr>
            </thead>
            <tbody>
              {data.data.map((bucket) => (
                <tr
                  key={bucket.id}
                  onClick={() => navigate(`/projects/${bucket.id}`)}
                  style={{ borderBottom: '1px solid var(--color-gray-100)', cursor: 'pointer', transition: 'background var(--duration-snap)' }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-bg-subtle)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = '')}
                >
                  <td style={{ padding: 'var(--space-3) var(--space-4)' }}>
                    <p style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)', color: 'var(--color-text)' }}>
                      {bucket.name}
                    </p>
                  </td>
                  <td style={{ padding: 'var(--space-3) var(--space-4)', fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
                    {bucket.companyName ?? '—'}
                  </td>
                  <td style={{ padding: 'var(--space-3) var(--space-4)' }}>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)',
                      color: STATUS_COLORS[bucket.status] ?? 'var(--color-text-secondary)',
                    }}>
                      <span style={{
                        width: 6, height: 6, borderRadius: '50%',
                        background: STATUS_COLORS[bucket.status] ?? 'var(--color-text-secondary)',
                        flexShrink: 0,
                      }} />
                      {t(`status.${bucket.status}`)}
                    </span>
                  </td>
                  <td style={{ padding: 'var(--space-3) var(--space-4)', fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
                    {bucket.value ? `${workspace?.currency ?? '$'} ${parseFloat(bucket.value).toLocaleString()}` : '—'}
                  </td>
                  <td style={{ padding: 'var(--space-3) var(--space-4)', fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
                    {bucket.expectedClose ?? '—'}
                  </td>
                  <td style={{ padding: 'var(--space-3) var(--space-4)', textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                    <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => { setEditingBucket(bucket); setModalOpen(true); }}
                        style={actionBtnStyle}
                        aria-label={`Edit ${bucket.name}`}
                      >
                        {t('detail.edit_button')}
                      </button>
                      <button
                        onClick={() => handleDelete(bucket)}
                        style={{ ...actionBtnStyle, color: 'var(--color-danger)' }}
                        aria-label={`Delete ${bucket.name}`}
                      >
                        {tc('actions.delete')}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {data.total > 50 && (
            <div style={{
              padding: 'var(--space-3) var(--space-4)', borderTop: '1px solid var(--color-border)',
              fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)',
            }}>
              {tc('pagination.showing', { from: 1, to: data.data.length, total: data.total })}
            </div>
          )}
        </div>
      )}

      {modalOpen && (
        <BucketModal bucket={editingBucket} onClose={handleModalClose} />
      )}
    </div>
  );
}

const actionBtnStyle: React.CSSProperties = {
  padding: '4px 8px', background: 'none', border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-sm)', fontSize: 'var(--text-xs)',
  color: 'var(--color-text-secondary)', cursor: 'pointer',
};

const selectStyle: React.CSSProperties = {
  padding: 'var(--space-2) var(--space-3)', border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)',
  color: 'var(--color-text)', background: 'var(--color-bg)',
};
